/**
 * zoc-detection system — v0M3.4.
 *
 * Detects Zone-of-Control overlaps between enemy Armies on the strategic
 * map and creates a Battle when two Armies of different teams enter ZoC
 * overlap per `MECHANICS.md` §3.2 (`zoc_radius` from `Balance.xlsx`,
 * currently 3.0 cells). Runs after `movement` in the fixed system order
 * (see `systems.ts`) so it sees each tick's updated Army positions.
 *
 * Overlap rule: two circular ZoCs of radius `R` centred on two Armies
 * overlap iff the Euclidean distance between the Armies is less than
 * `2 × R`. Equal-distance edge case (`d == 2R`) counts as *touching*
 * (not overlapping) — the tests use strict inequality below the threshold
 * to trigger a battle.
 *
 * Pair-selection contract:
 *  - Iterate Army pairs deterministically via `sortedIds()` (i < j).
 *  - Skip if both armies belong to the same team (`Faction.teamId`).
 *  - Skip if either army is already in a battle (`currentBattleId !== null`).
 *  - On overlap: call `createBattleFromArmies` (see `battle-factory.ts`)
 *    which atomically hydrates the Battle *and* sets the engagement lock
 *    on both armies (`currentBattleId` + cleared `plannedPath`).
 *
 * Bilateral only at v0M3.4 — a third Army entering the same overlap on a
 * later tick will not merge; it will simply fail the "both free" check
 * and stay pending until one side of the current battle concludes. v1
 * design supports N-sided engagements per §2.7.1; the merge / join
 * mechanic is a follow-up milestone.
 */

import type { Command } from './commands';
import type { Army, GameState } from './state';
import type { RngState } from './rng';
import type { SimContext } from './sim-context';
import { sortedIds } from './state';
import { createBattleFromArmies, createSiegeBattleFromArmies } from './battle-factory';
import { destroyAllCohortsOfArmy, disbandArmy } from './battle-conclusion';
import { getDialNumber, loadDials } from '../data/dials';
import { sortedIds as sortedIdsGeneric } from './state';

// Cache dials at module load — Balance.xlsx values are immutable at runtime.
const DIALS = loadDials();
const ZOC_RADIUS = getDialNumber(DIALS, 'zoc_radius');
// MFix5+fix8 (2026-07-23): radius (Chebyshev cells) within which an
// army-vs-army battle spawns on siege terrain (walls + gates) instead
// of open-field terrain. The friendly-city defender holds the walls;
// the attacker is placed outside.
const SETTLEMENT_BATTLE_TRIGGER_RADIUS = getDialNumber(DIALS, 'settlement_battle_trigger_radius');
// MFix1.5: weak-army stamina threshold. Armies where every cohort has
// stamina ≤ this value cannot participate in a new battle — instead they
// are destroyed on ZoC contact per Michael's playtest fix.
const WEAK_ARMY_MAX_STAMINA = 10;
// MFix15 (2026-07-29): morale twin of the stamina threshold. An army
// whose cohorts are all at or below this morale value cannot fight
// either — same rationale as MFix1.5 (exhausted armies re-engaging in
// a loop). Michael's report: 3 cohorts at 0 morale kept re-engaging
// because they still had stamina; a cohort with 0 morale is broken
// per BATTLES.md §2.7.8.5.c and shouldn't count as combat-capable.
const WEAK_ARMY_MAX_MORALE = 10;
/**
 * Two ZoC circles of radius R touch when their centres are exactly at
 * distance 2R. v0M3.5: movement clamps armies to touching-distance; the
 * battle-create check fires at touching-or-closer (small epsilon to
 * absorb float error from the clamp math).
 */
const TOUCH_THRESHOLD = 2 * ZOC_RADIUS;
const TOUCH_EPSILON = 1e-3;

export function zocDetectionSystem(
  state: GameState,
  _commands: readonly Command[],
  _rng: RngState,
  context: SimContext,
): GameState {
  const ids = sortedIds(state.armies);
  const n = ids.length;

  for (let i = 0; i < n; i++) {
    // MFix1.5: an army may have been deleted mid-iteration by the
    // weak-army destruction path below. `ids` was snapshotted before
    // the loop, so a stale id here means the army is gone. Skip it.
    const a = state.armies.get(ids[i]!);
    if (!a) continue;
    // A single army may be picked into a battle inside this loop; if so,
    // its currentBattleId is set and further pairs involving it are skipped.
    if (a.currentBattleId !== null) continue;
    const factionA = state.factions.get(a.ownerFactionId);
    if (!factionA) continue;

    for (let j = i + 1; j < n; j++) {
      const b = state.armies.get(ids[j]!);
      if (!b) continue; // MFix1.5: may have been destroyed on a prior pair.
      if (b.currentBattleId !== null) continue;
      const factionB = state.factions.get(b.ownerFactionId);
      if (!factionB) continue;

      // Same team → no engagement (MECHANICS.md §3.2 + §7 team rule).
      if (factionA.teamId === factionB.teamId) continue;

      // Euclidean distance check — trigger at touching (distance <= 2R)
      // per v0M3.5 (was strictly < before; movement.ts now clamps to
      // touching so we need <= here).
      const dx = a.position.x - b.position.x;
      const dy = a.position.y - b.position.y;
      const distSq = dx * dx + dy * dy;
      const thresholdSq = (TOUCH_THRESHOLD + TOUCH_EPSILON) * (TOUCH_THRESHOLD + TOUCH_EPSILON);
      if (distSq > thresholdSq) continue;

      // MFix1.5: weak-army rule per Michael's playtest fix (2026-07-16).
      // An army whose strongest cohort has stamina ≤ WEAK_ARMY_MAX_STAMINA
      // (10) cannot fight — it's destroyed on ZoC contact instead of
      // spawning a battle. Both weak → both destroyed. Only one weak →
      // just the weak side destroyed; the strong army is not engaged
      // (no battle spawned, no engagement lock). This breaks the
      // playtest cycle where exhausted armies re-engaged, fled, and
      // re-engaged again indefinitely.
      const aWeak = isArmyWeak(a, state);
      const bWeak = isArmyWeak(b, state);
      if (aWeak || bWeak) {
        if (aWeak) {
          destroyAllCohortsOfArmy(state, a);
          disbandArmy(state, a);
        }
        if (bWeak) {
          destroyAllCohortsOfArmy(state, b);
          disbandArmy(state, b);
        }
        // If army `a` is gone, break the inner loop; the outer loop
        // will skip its slot anyway on next iteration.
        if (aWeak) break;
        continue;
      }

      // MFix5+fix8 (2026-07-23): if either army sits within
      // `settlement_battle_trigger_radius` of a city owned by their own
      // faction, spawn a siege battle instead of an open-field one so
      // the friendly-city defender fights from behind walls. Falls back
      // to open field if no army has a nearby friendly city (both
      // hostile to any nearby city, or no city nearby at all).
      const cityBattleSetup = pickFriendlyCityBattleSide(state, a, b, factionA, factionB);
      let battle;
      if (cityBattleSetup !== null) {
        battle = createSiegeBattleFromArmies(
          state,
          cityBattleSetup.attackerArmy,
          cityBattleSetup.city,
          cityBattleSetup.defenderArmy,
          context,
        );
      } else {
        // Overlap — create the battle. `createBattleFromArmies` sets the
        // engagement lock on both armies atomically. v0M4.0j.1: pass
        // context so battlefield terrain samples from the strategic map.
        battle = createBattleFromArmies(state, a, b, context);
      }
      state.battles.set(battle.id, battle);

      // `a` is now in a battle — break the inner loop; the outer-loop
      // `continue` check on `currentBattleId` will skip it next iteration
      // anyway, but breaking here avoids one wasted distance calc.
      break;
    }
  }

  return state;
}

/**
 * MFix5+fix8 (2026-07-23) — Decide whether to spawn a siege-terrain
 * battle instead of an open-field battle. Returns the
 * (attacker, defender, city) triple when one of the two armies is
 * within `SETTLEMENT_BATTLE_TRIGGER_RADIUS` of a city owned by their OWN
 * faction (Chebyshev distance). Returns null otherwise.
 *
 * Tie-break rules when both armies are near friendly cities:
 *  - Prefer the smaller Chebyshev distance (defender is right at the walls).
 *  - Ties break by lower army id (deterministic).
 *
 * "Own faction" means the exact `ownerFactionId` — same team but
 * different faction doesn't qualify. Post-v1 alliance layer can revisit.
 */
function pickFriendlyCityBattleSide(
  state: GameState,
  a: Army,
  b: Army,
  _factionA: import('./state').Faction,
  _factionB: import('./state').Faction,
): { attackerArmy: Army; defenderArmy: Army; city: import('./state').Settlement } | null {
  const nearestA = nearestOwnCity(state, a);
  const nearestB = nearestOwnCity(state, b);
  if (nearestA === null && nearestB === null) return null;

  // Choose the closest defender-friendly pairing. Prefer smaller distance;
  // tie-break by lower army id (a's id < b's id per the outer i < j loop).
  let defender: Army;
  let attacker: Army;
  let city: import('./state').Settlement;
  if (nearestA !== null && (nearestB === null || nearestA.dist <= nearestB.dist)) {
    defender = a;
    attacker = b;
    city = nearestA.city;
  } else if (nearestB !== null) {
    defender = b;
    attacker = a;
    city = nearestB.city;
  } else {
    return null; // Defensive; the earlier null-check should have caught this.
  }
  return { attackerArmy: attacker, defenderArmy: defender, city };
}

/**
 * MFix5+fix8: find the nearest city owned by `army.ownerFactionId`
 * within `SETTLEMENT_BATTLE_TRIGGER_RADIUS` Chebyshev cells of the army's
 * position. Returns null if no such city exists.
 */
function nearestOwnCity(
  state: GameState,
  army: Army,
): { city: import('./state').Settlement; dist: number } | null {
  let best: { city: import('./state').Settlement; dist: number } | null = null;
  for (const settlementId of sortedIdsGeneric(state.settlements)) {
    const city = state.settlements.get(settlementId);
    if (!city) continue;
    const province = state.provinces.get(city.provinceId);
    if (!province) continue;
    if (province.ownerFactionId !== army.ownerFactionId) continue;
    const dx = Math.abs(army.position.x - city.cell.x);
    const dy = Math.abs(army.position.y - city.cell.y);
    const cheb = Math.max(dx, dy);
    if (cheb > SETTLEMENT_BATTLE_TRIGGER_RADIUS) continue;
    if (best === null || cheb < best.dist) {
      best = { city, dist: cheb };
    }
  }
  return best;
}

/**
 * MFix1.5 + MFix15: an army is "weak" iff every cohort in its roster is
 * combat-broken — meaning stamina ≤ WEAK_ARMY_MAX_STAMINA (10) OR
 * morale ≤ WEAK_ARMY_MAX_MORALE (10). Either condition alone means the
 * cohort cannot meaningfully fight (broken per BATTLES.md §2.7.8.5.c).
 * A cohort with EITHER stat > threshold is still combat-capable, so the
 * army as a whole isn't weak.
 *
 * Cohorts that broke below the threshold during a prior battle stay
 * weak until map-side recovery lifts them back above per v0M3.11 /
 * v0M3.4b rates. Empty-roster armies aren't treated as weak here
 * (they'd have been disbanded already).
 */
function isArmyWeak(army: Army, state: GameState): boolean {
  if (army.cohortIds.length === 0) return false;
  for (const cohortId of army.cohortIds) {
    const cohort = state.cohorts.get(cohortId);
    if (!cohort) continue;
    // MFix15: BOTH stamina AND morale must exceed their thresholds for
    // this cohort to count as combat-capable. If either is at/below,
    // the cohort is broken.
    if (
      cohort.stamina > WEAK_ARMY_MAX_STAMINA &&
      cohort.morale > WEAK_ARMY_MAX_MORALE
    ) {
      return false;
    }
  }
  return true;
}
